import React from 'react';

import HelpTooltip from './HelpTooltip';

interface ResolutionOption {
  label: string;
  width: number;
  height: number;
}

interface ResolutionWidgetProps {
  widthValue: number;
  heightValue: number;
  onWidthChange: (value: number) => void;
  onHeightChange: (value: number) => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  description?: string;
  helpText?: string;
}

// Common resolution presets
const RESOLUTION_PRESETS: ResolutionOption[] = [
  // Square
  { label: '512×512 (Square)', width: 512, height: 512 },
  { label: '768×768 (Square)', width: 768, height: 768 },
  { label: '1024×1024 (Square)', width: 1024, height: 1024 },
  
  // Landscape 16:9
  { label: '1280×720 (HD 16:9)', width: 1280, height: 720 },
  { label: '1920×1080 (Full HD 16:9)', width: 1920, height: 1080 },
  
  // Landscape 4:3
  { label: '1024×768 (4:3)', width: 1024, height: 768 },
  { label: '1152×896 (4:3)', width: 1152, height: 896 },
  
  // Landscape 3:2
  { label: '1216×832 (3:2)', width: 1216, height: 832 },
  
  // Landscape 21:9
  { label: '1536×640 (Cinematic 21:9)', width: 1536, height: 640 },
  { label: '1344×768 (21:9)', width: 1344, height: 768 },
  
  // Portrait 16:9
  { label: '720×1280 (Portrait 16:9)', width: 720, height: 1280 },
  { label: '1080×1920 (Portrait 16:9)', width: 1080, height: 1920 },
  
  // Portrait 4:3
  { label: '768×1024 (Portrait 4:3)', width: 768, height: 1024 },
  { label: '896×1152 (Portrait 4:3)', width: 896, height: 1152 },
  
  // Portrait 3:2
  { label: '832×1216 (Portrait 3:2)', width: 832, height: 1216 },
  
  // Portrait 9:16
  { label: '768×1344 (Portrait 9:16)', width: 768, height: 1344 },
  
  // Ultra-wide
  { label: '2048×1024 (Ultra-wide)', width: 2048, height: 1024 },
];

const ResolutionWidget: React.FC<ResolutionWidgetProps> = ({
  widthValue,
  heightValue,
  onWidthChange,
  onHeightChange,
  disabled = false,
  error,
  label = 'Resolution',
  description,
  helpText
}) => {
  // Ensure we have valid numeric values (Safari compatibility)
  const safeWidthValue = typeof widthValue === 'number' && !isNaN(widthValue) ? widthValue : 1024;
  const safeHeightValue = typeof heightValue === 'number' && !isNaN(heightValue) ? heightValue : 1024;
  
  // Find matching preset or use "Custom"
  const currentResolution = RESOLUTION_PRESETS.find(
    preset => preset.width === safeWidthValue && preset.height === safeHeightValue
  );
  
  const currentValue = currentResolution 
    ? `${currentResolution.width}x${currentResolution.height}`
    : 'custom';

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    if (value === 'custom') {
      // Don't change values for custom
      return;
    }
    
    const [widthStr, heightStr] = value.split('x');
    const width = parseInt(widthStr, 10);
    const height = parseInt(heightStr, 10);
    
    if (!isNaN(width) && !isNaN(height)) {
      onWidthChange(width);
      onHeightChange(height);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <label className="text-label font-medium text-gray-700">
          {label}{error && <span className="text-error ml-0.5"> *</span>}
        </label>
        {helpText && <HelpTooltip helpText={helpText} />}
      </div>
      {description && <p className="text-label text-gray-500">{description}</p>}
      <select aria-label={label} value={currentValue} onChange={handleChange} disabled={disabled}
        className={`w-full px-3 py-2.5 border rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input disabled:bg-gray-100 ${error ? 'border-error' : 'border-gray-300'}`}>
        {RESOLUTION_PRESETS.map(preset => (
          <option key={`${preset.width}x${preset.height}`} value={`${preset.width}x${preset.height}`}>
            {preset.label}
          </option>
        ))}
        {!currentResolution && <option value="custom">Custom ({safeWidthValue}×{safeHeightValue})</option>}
      </select>
      {error && <p className="text-label text-error">{error}</p>}
      <p className="text-meta text-gray-400">Width: {safeWidthValue} × Height: {safeHeightValue}</p>
    </div>
  );
};

export { ResolutionWidget };
export default ResolutionWidget;
